import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { projectsApi, orchestratorApi, analyticsApi } from '../utils/api';
import { useAuthStore } from '../store/auth';
import {
    FolderKanban,
    TrendingUp,
    DollarSign,
    Activity,
    Plus,
    ArrowRight,
    CheckCircle,
    AlertCircle,
    RefreshCw,
    Zap,
    Search,
    Mail,
    BarChart3,
    CreditCard,
    Settings,
    Sparkles
} from 'lucide-react';
import { StatsSkeleton, ProjectsSkeleton, HealthSkeleton, ApiError } from '../components/ErrorBoundary';

// Format large numbers for display
function formatNumber(num: number): string {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
}

function formatCurrency(num: number): string {
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
    return `$${num.toFixed(0)}`;
}

export default function Dashboard() {
    const { user } = useAuthStore();

    const {
        data: projectsData,
        isLoading: projectsLoading,
        error: projectsError,
        refetch: refetchProjects
    } = useQuery({
        queryKey: ['projects'],
        queryFn: () => projectsApi.list(),
    });

    const {
        data: healthData,
        isLoading: healthLoading,
        error: healthError,
        refetch: refetchHealth
    } = useQuery({
        queryKey: ['health'],
        queryFn: () => orchestratorApi.getHealth(),
        refetchInterval: 30000,
    });

    // Aggregate dashboard stats from all projects
    const {
        data: aggregateStats,
        isLoading: statsLoading,
        error: statsError,
        refetch: refetchStats
    } = useQuery({
        queryKey: ['dashboard-aggregate'],
        queryFn: async () => {
            // Get aggregate stats - in production this would be a single API call
            // For now we calculate from available data
            const projects = projectsData?.data?.projects || [];
            let totalTraffic = 0;
            let totalRevenue = 0;
            let activeCampaigns = 0;

            // Fetch dashboard data for each project (limit to first 5 for performance)
            const dashboardPromises = projects.slice(0, 5).map(async (p: any) => {
                try {
                    const res = await analyticsApi.getDashboard(p.id);
                    return res.data?.dashboard;
                } catch {
                    return null;
                }
            });

            const dashboards = await Promise.all(dashboardPromises);
            dashboards.forEach((d) => {
                if (d?.overview) {
                    totalTraffic += d.overview.totalTraffic || 0;
                    totalRevenue += d.overview.totalRevenue || 0;
                    activeCampaigns += d.overview.activeCampaigns || 0;
                }
            });

            return { totalTraffic, totalRevenue, activeCampaigns };
        },
        enabled: !!projectsData?.data?.projects?.length,
        staleTime: 60000, // Cache for 1 minute
    });

    const projects = projectsData?.data?.projects || [];
    const health = healthData?.data;

    const stats = [
        {
            label: 'Total Projects',
            value: projects.length,
            icon: FolderKanban,
            color: 'bg-blue-500',
        },
        {
            label: 'Active Campaigns',
            value: aggregateStats?.activeCampaigns ?? 0,
            icon: Activity,
            color: 'bg-green-500',
        },
        {
            label: 'Total Traffic',
            value: formatNumber(aggregateStats?.totalTraffic ?? 0),
            icon: TrendingUp,
            color: 'bg-purple-500',
        },
        {
            label: 'Revenue',
            value: formatCurrency(aggregateStats?.totalRevenue ?? 0),
            icon: DollarSign,
            color: 'bg-orange-500',
        },
    ];

    const isLoadingStats = projectsLoading || statsLoading;

    // Quick actions for dashboard
    const quickActions = [
        { label: 'New Project', icon: Plus, to: '/projects', color: 'from-blue-500 to-cyan-500' },
        { label: 'Run SEO Audit', icon: Search, to: projects[0] ? `/projects/${projects[0].id}/seo` : '/projects', color: 'from-green-500 to-emerald-500' },
        { label: 'Send Campaign', icon: Mail, to: projects[0] ? `/projects/${projects[0].id}/email` : '/projects', color: 'from-purple-500 to-violet-500' },
        { label: 'View Analytics', icon: BarChart3, to: projects[0] ? `/projects/${projects[0].id}/analytics` : '/projects', color: 'from-orange-500 to-amber-500' },
        { label: 'Manage Billing', icon: CreditCard, to: '/settings/billing', color: 'from-pink-500 to-rose-500' },
        { label: 'Settings', icon: Settings, to: '/settings', color: 'from-dark-500 to-dark-600' },
    ];

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Welcome back, {user?.name?.split(' ')[0]}!</h1>
                    <p className="text-dark-400 mt-1">Here's what's happening with your projects today.</p>
                </div>
                <button
                    onClick={() => {
                        refetchProjects();
                        refetchStats();
                        refetchHealth();
                    }}
                    className="btn btn-secondary flex items-center gap-2"
                    title="Refresh data"
                >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {/* Quick Actions */}
            <div className="card bg-gradient-to-br from-dark-800 to-dark-900 border-dark-700">
                <div className="flex items-center gap-2 mb-4">
                    <Zap className="w-5 h-5 text-yellow-400" />
                    <h2 className="font-semibold">Quick Actions</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {quickActions.map((action) => (
                        <Link
                            key={action.label}
                            to={action.to}
                            className="flex flex-col items-center gap-2 p-4 rounded-xl bg-dark-800/50 hover:bg-dark-700/50 transition-all hover:scale-105 group"
                        >
                            <div className={`w-10 h-10 bg-gradient-to-br ${action.color} rounded-lg flex items-center justify-center group-hover:shadow-lg transition-shadow`}>
                                <action.icon className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xs text-dark-300 text-center">{action.label}</span>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Stats Error */}
            {(projectsError || statsError) && (
                <ApiError
                    error={projectsError as Error || statsError as Error}
                    onRetry={() => { refetchProjects(); refetchStats(); }}
                    message="Failed to load dashboard stats"
                />
            )}

            {/* Stats Grid */}
            {isLoadingStats ? (
                <StatsSkeleton />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {stats.map((stat) => (
                        <div key={stat.label} className="card">
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                                    <stat.icon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stat.value}</p>
                                    <p className="text-dark-400 text-sm">{stat.label}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Recent Projects */}
            <div className="card">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold">Recent Projects</h2>
                    <Link to="/projects" className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1">
                        View all <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                {projectsLoading ? (
                    <ProjectsSkeleton />
                ) : projectsError ? (
                    <ApiError
                        error={projectsError as Error}
                        onRetry={refetchProjects}
                        message="Failed to load projects"
                    />
                ) : projects.length === 0 ? (
                    <div className="text-center py-12">
                        <FolderKanban className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">No projects yet</h3>
                        <p className="text-dark-400 mb-4">Create your first project to get started</p>
                        <Link to="/projects" className="btn btn-primary inline-flex items-center gap-2">
                            <Plus className="w-5 h-5" />
                            Create Project
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {projects.slice(0, 5).map((project: any) => (
                            <Link
                                key={project.id}
                                to={`/projects/${project.id}`}
                                className="flex items-center justify-between p-4 rounded-lg bg-dark-800/50 hover:bg-dark-800 transition-colors"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-primary-600/20 rounded-lg flex items-center justify-center">
                                        <FolderKanban className="w-5 h-5 text-primary-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium">{project.name}</h3>
                                        <p className="text-dark-400 text-sm">{project.websiteDomain || 'No website'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${project.status === 'active'
                                        ? 'bg-green-500/20 text-green-400'
                                        : 'bg-dark-600 text-dark-400'
                                        }`}>
                                        {project.status}
                                    </span>
                                    <ArrowRight className="w-5 h-5 text-dark-500" />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>

            {/* System Health */}
            <div className="card">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold">System Health</h2>
                    <button
                        onClick={() => refetchHealth()}
                        className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                </div>

                {healthError && (
                    <ApiError
                        error={healthError as Error}
                        onRetry={refetchHealth}
                        message="Failed to load system health"
                    />
                )}

                {healthLoading ? (
                    <HealthSkeleton />
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        {health?.services ? (
                            Object.entries(health.services).map(([service, status]) => (
                                <div key={service} className="flex items-center gap-2 p-3 rounded-lg bg-dark-800/50">
                                    {status === 'healthy' ? (
                                        <CheckCircle className="w-5 h-5 text-green-400" />
                                    ) : (
                                        <AlertCircle className="w-5 h-5 text-red-400" />
                                    )}
                                    <span className="text-sm capitalize">{service}</span>
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full text-center text-dark-400 py-4">
                                No health data available
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Getting Started (show only if no projects or few projects) */}
            {projects.length < 3 && (
                <div className="card bg-gradient-to-br from-primary-900/20 to-purple-900/20 border-primary-800/30">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="font-semibold">Getting Started with aiOps</h2>
                            <p className="text-sm text-dark-400">Complete these steps to unlock the full potential</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            {
                                done: projects.length > 0,
                                label: 'Create your first project',
                                link: '/projects',
                                description: 'Set up a project to start using AI modules'
                            },
                            {
                                done: false,
                                label: 'Run an SEO audit',
                                link: projects[0] ? `/projects/${projects[0].id}/seo` : '/projects',
                                description: 'Analyze your website for SEO improvements'
                            },
                            {
                                done: false,
                                label: 'Connect your API keys',
                                link: '/settings',
                                description: 'Add OpenAI or Anthropic keys for AI features'
                            },
                            {
                                done: false,
                                label: 'Explore modules',
                                link: '/settings/billing',
                                description: 'Browse available modules and bundles'
                            },
                        ].map((step, index) => (
                            <Link
                                key={index}
                                to={step.link}
                                className={`p-4 rounded-xl border transition-all hover:scale-[1.02] ${step.done
                                        ? 'bg-green-500/10 border-green-500/30'
                                        : 'bg-dark-800/50 border-dark-700 hover:border-primary-500/50'
                                    }`}
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    {step.done ? (
                                        <CheckCircle className="w-5 h-5 text-green-400" />
                                    ) : (
                                        <div className="w-5 h-5 rounded-full border-2 border-dark-500" />
                                    )}
                                    <span className={`text-sm font-medium ${step.done ? 'text-green-400' : ''}`}>
                                        {step.label}
                                    </span>
                                </div>
                                <p className="text-xs text-dark-400 ml-7">{step.description}</p>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}